import type { Container } from "inversify";
import * as THREE from "three";
import { Logger, Module } from "@designboticteam/core";
import { MapLibreParcelsDataService } from "@designboticteam/maplibre";
import { type Shape, ShapeEditingService, ShapeTool, type ToolClass, ToolsManagerService, Workspace3DStoreService } from "@designboticteam/workspace-3d";
import { Kolega3DStoreService, type ParcelSelectionMode } from "./services/Kolega3DStoreService";
import { ParcelPickerService } from "./services/ParcelPickerService";
import { SnapshotService } from "./services/SnapshotService";
import { ValidationService } from "./services/ValidationService";
import { BuildingLineTool } from "./tools/BuildingLineTool";
import { ProjectAreaTool } from "./tools/ProjectAreaTool";
import { type Zone, ZoneTool, type ZoneType } from "./tools/ZoneTool";

type Kolega3DModuleConfig = {
	projectAreaMode?: "manual" | "auto";
	autoProjectAreaMargin?: number;
	singleCheckZone?: boolean;
	editZoneAfterAccept?: boolean;
};

const DEFAULT_AUTO_PROJECT_AREA_MARGIN = 50;

type KolegaToolClass = typeof ProjectAreaTool | typeof ZoneTool | typeof BuildingLineTool;

const KOLEGA_3D_TOOLS: Array<ToolClass> = [ProjectAreaTool, ZoneTool, BuildingLineTool];

class Kolega3DModule extends Module {
	public readonly moduleName = "Kolega3DModule";
	private readonly logger = new Logger(`🏗️ ${this.moduleName}`, { color: "#ffffff", backgroundColor: "#1d4ed8" });

	private readonly unsubscribers: Array<() => void> = [];

	constructor(private readonly config: Kolega3DModuleConfig = {}) {
		super();
		this.services = [Kolega3DStoreService, ParcelPickerService, ValidationService, SnapshotService];
	}

	public override register(container: Container): void {
		super.register(container);

		for (const Tool of KOLEGA_3D_TOOLS) {
			container.bind(Tool).toSelf().inSingletonScope();
		}
	}

	public override initialize(): void {
		super.initialize();

		this.getService(MapLibreParcelsDataService).setHoverable(false);

		const toolsManagerService = this.getService(ToolsManagerService);
		for (const Tool of KOLEGA_3D_TOOLS) {
			toolsManagerService.registerTool(this.container.get(Tool));
		}

		this.getTool(ZoneTool).configure({
			singleCheckZone: this.config.singleCheckZone,
			editAfterAccept: this.config.editZoneAfterAccept,
		});

		if (this.config.projectAreaMode === "auto") {
			this.unsubscribers.push(
				this.getService(Kolega3DStoreService).store.subscribe(
					(state) => state.zones,
					(zones) => this.syncAutoProjectArea(zones),
				),
			);
		}

		this.logger.log("initialized");
	}

	public override dispose(): void {
		for (const unsubscribe of this.unsubscribers) {
			unsubscribe();
		}

		this.unsubscribers.length = 0;
		super.dispose();
	}

	private syncAutoProjectArea(zones: ReadonlyArray<Zone>): void {
		this.fitProjectAreaToZones(zones);
	}

	private fitProjectAreaToZones(zones: ReadonlyArray<Zone>): void {
		const projectAreaTool = this.getTool(ProjectAreaTool);
		const points = zones.flatMap((zone) => zone.getPoints());

		if (points.length === 0) {
			projectAreaTool.clearAll();
			return;
		}

		const center = new THREE.Box3().setFromPoints(points).getCenter(new THREE.Vector3());
		center.y = 0;

		const margin = this.config.autoProjectAreaMargin ?? DEFAULT_AUTO_PROJECT_AREA_MARGIN;
		const radius = Math.max(...points.map((point) => point.distanceTo(center))) + margin;

		projectAreaTool.setProjectArea({ center, radius });
	}

	public get api() {
		return {
			kolega3D: {
				store: this.getService(Kolega3DStoreService).store,
				snapshot: () => this.getService(SnapshotService).getSnapshot(),
				tools: {
					setActiveTool: (Tool: KolegaToolClass | null) => {
						this.getService(ToolsManagerService).setActiveTool(Tool);
					},
					clearAll: () => {
						const toolsManagerService = this.getService(ToolsManagerService);
						const activeTool = toolsManagerService.activeTool;

						if (activeTool instanceof ShapeTool) {
							activeTool.cancelDrawing();
						}

						toolsManagerService.setActiveTool(null);
						this.getService(ShapeEditingService).clearAll();
						this.getTool(ProjectAreaTool).clearAll();
						this.getTool(ZoneTool).clearAll();
						this.getTool(BuildingLineTool).clearAll();
						this.getTool(ZoneTool).clearParcelSelection();
					},
					deleteSelectedShape: () => {
						const selectedShape = this.getService(Workspace3DStoreService).store.getState().tools.selectedShape;

						if (selectedShape === null) {
							return;
						}

						this.getService(ShapeEditingService).unregisterShape(selectedShape);
						this.getTool(ProjectAreaTool).deleteShape(selectedShape);
						this.getTool(ZoneTool).deleteShape(selectedShape);
						this.getTool(BuildingLineTool).deleteShape(selectedShape);
					},
				},
				projectArea: {
					fitToZones: () => {
						this.fitProjectAreaToZones(this.getService(Kolega3DStoreService).store.getState().zones);
					},
					clear: () => {
						this.getTool(ProjectAreaTool).clearAll();
					},
					startEditing: () => {
						this.getTool(ProjectAreaTool).startEditing();
					},
				},
				zones: {
					startDrawing: () => {
						this.getTool(ZoneTool).startDrawing();
					},
					startPicking: () => {
						this.getTool(ZoneTool).startPicking();
					},
					startEditing: (zone: Zone) => {
						this.getTool(ZoneTool).startEditing(zone);
					},
					removeOfType: (zoneType: ZoneType) => {
						this.getTool(ZoneTool).removeZonesOfType(zoneType);
					},
					setDraftType: (zoneType: ZoneType) => {
						this.getTool(ZoneTool).setDraftZoneType(zoneType);
					},
					getDraftType: (): ZoneType => this.getTool(ZoneTool).getDraftZoneType(),
					getType: (shape: Shape): ZoneType | null => this.getTool(ZoneTool).getZoneType(shape),
					changeSelectedType: (zoneType: ZoneType) => {
						this.getTool(ZoneTool).changeSelectedZoneType(zoneType);
					},
					cloneSelectedWithOffset: (offset: number) => {
						this.getTool(ZoneTool).cloneSelectedZoneWithOffset(offset);
					},
					createOffset: ({ sourceZone, zoneType, offset }: { sourceZone: Zone; zoneType: ZoneType; offset: number }) => {
						this.getTool(ZoneTool).createOffsetZone({ sourceZone, zoneType, offset });
					},
				},
				parcels: {
					setSelectionMode: (mode: ParcelSelectionMode) => {
						this.getTool(ZoneTool).setParcelSelectionMode(mode);
					},
					removeSelected: (id: string) => {
						this.getTool(ZoneTool).removeSelectedParcel(id);
					},
					clearSelection: () => {
						this.getTool(ZoneTool).clearParcelSelection();
					},
					createZone: ({ zoneType }: { zoneType: ZoneType }) => {
						this.getTool(ZoneTool).createZonesFromParcelSelection({ zoneType });
					},
				},
			},
		};
	}
}

export { Kolega3DModule, type Kolega3DModuleConfig, type KolegaToolClass };
